import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Building, User, FileText, CreditCard, CheckCircle, AlertCircle, Upload, X, Shield, ChevronRight, Info } from 'lucide-react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { useToast } from '../../hooks/useToast';
type OnboardingStep = 'identity' | 'ownership' | 'banking' | 'review';
interface IdentityData {
  documentType: 'nin' | 'passport' | 'drivers_license';
  documentNumber: string;
  documentFile?: File;
}
interface OwnershipData {
  documentType: 'title_deed' | 'certificate_of_occupancy' | 'purchase_agreement';
  propertyAddress: string;
  documentFile?: File;
}
interface BankingData {
  accountNumber: string;
  bankName: string;
  accountName: string;
}
export function LandlordOnboarding() {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('identity');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form data
  const [identityData, setIdentityData] = useState<IdentityData>({
    documentType: 'nin',
    documentNumber: ''
  });
  const [ownershipData, setOwnershipData] = useState<OwnershipData[]>([{
    documentType: 'title_deed',
    propertyAddress: ''
  }]);
  const [bankingData, setBankingData] = useState<BankingData>({
    accountNumber: '',
    bankName: '',
    accountName: ''
  });
  const steps = [{
    id: 'identity',
    label: 'Identity Verification',
    icon: User
  }, {
    id: 'ownership',
    label: 'Property Ownership',
    icon: Building
  }, {
    id: 'banking',
    label: 'Bank Account',
    icon: CreditCard
  }, {
    id: 'review',
    label: 'Review & Submit',
    icon: FileText
  }];
  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const handleFileUpload = (file: File, type: 'identity' | 'ownership', index?: number) => {
    if (type === 'identity') {
      setIdentityData({
        ...identityData,
        documentFile: file
      });
    } else if (type === 'ownership' && index !== undefined) {
      const newOwnership = [...ownershipData];
      newOwnership[index].documentFile = file;
      setOwnershipData(newOwnership);
    }
    toast.success('Document uploaded successfully');
  };
  const addOwnershipDocument = () => {
    setOwnershipData([...ownershipData, {
      documentType: 'title_deed',
      propertyAddress: ''
    }]);
  };
  const removeOwnershipDocument = (index: number) => {
    setOwnershipData(ownershipData.filter((_, i) => i !== index));
  };
  const canProceed = () => {
    switch (currentStep) {
      case 'identity':
        return identityData.documentNumber && identityData.documentFile;
      case 'ownership':
        return ownershipData.every(doc => doc.propertyAddress && doc.documentFile);
      case 'banking':
        return bankingData.accountNumber && bankingData.bankName && bankingData.accountName;
      case 'review':
        return true;
      default:
        return false;
    }
  };
  const handleNext = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex].id as OnboardingStep);
    }
  };
  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].id as OnboardingStep);
    }
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    toast.success('Verification request submitted! Our team will review within 24 hours.');
    setIsSubmitting(false);
    setTimeout(() => {
      navigate('/landlord/dashboard');
    }, 1500);
  };
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 pb-16 lg:pb-0" data-id="element-3672">
      <Sidebar data-id="element-3673" />

      <main className="flex-1 lg:ml-[240px]" data-id="element-3674">
        <DashboardHeader data-id="element-3675" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3676">
          {/* Header */}
          <div className="mb-8" data-id="element-3677">
            <div className="flex items-center space-x-3 mb-4" data-id="element-3678">
              <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center" data-id="element-3679">
                <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-3680" />
              </div>
              <div data-id="element-3681">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3682">
                  Landlord Verification
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3683">
                  Complete verification to list properties
                </p>
              </div>
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4" data-id="element-3684">
              <div className="flex items-start space-x-3" data-id="element-3685">
                <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" data-id="element-3686" />
                <div className="flex-1" data-id="element-3687">
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1" data-id="element-3688">
                    Renta Assisted Onboarding
                  </h3>
                  <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed" data-id="element-3689">
                    Our team will personally verify your documents within 24
                    hours. This ensures trust and safety for all tenants on the
                    platform.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="mb-8" data-id="element-3690">
            <div className="flex items-center justify-between" data-id="element-3691">
              {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = index < currentStepIndex;
              return <Fragment key={step.id} data-id="element-3692">
                    <div className="flex flex-col items-center flex-1" data-id="element-3693">
                      <motion.div initial={false} animate={{
                    scale: isActive ? 1.1 : 1,
                    backgroundColor: isCompleted ? '#10b981' : isActive ? '#3b82f6' : '#e5e7eb'
                  }} className={`h-12 w-12 rounded-full flex items-center justify-center mb-2 ${isCompleted ? 'bg-green-500' : isActive ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-3694">
                        {isCompleted ? <CheckCircle className="h-6 w-6 text-white" data-id="element-3695" /> : <StepIcon className={`h-6 w-6 ${isActive ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-3696" />}
                      </motion.div>
                      <p className={`text-xs font-medium text-center ${isActive ? 'text-blue-600 dark:text-blue-400' : isCompleted ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-3697">
                        {step.label}
                      </p>
                    </div>
                    {index < steps.length - 1 && <div className={`h-0.5 flex-1 mx-2 mt-[-20px] ${index < currentStepIndex ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-3698" />}
                  </Fragment>;
            })}
            </div>
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait" data-id="element-3699">
            <motion.div key={currentStep} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6" data-id="element-3700">
              {/* Identity Verification */}
              {currentStep === 'identity' && <div className="space-y-6" data-id="element-3701">
                  <div data-id="element-3702">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-3703">
                      Identity Verification
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3704">
                      Verify your identity with a government-issued document
                    </p>
                  </div>

                  <div data-id="element-3705">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3706">
                      Document Type *
                    </label>
                    <select value={identityData.documentType} onChange={e => setIdentityData({
                  ...identityData,
                  documentType: e.target.value as any
                })} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3707">
                      <option value="nin" data-id="element-3708">
                        National Identity Number (NIN)
                      </option>
                      <option value="passport" data-id="element-3709">International Passport</option>
                      <option value="drivers_license" data-id="element-3710">Driver's License</option>
                    </select>
                  </div>

                  <div data-id="element-3711">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3712">
                      Document Number *
                    </label>
                    <input type="text" value={identityData.documentNumber} onChange={e => setIdentityData({
                  ...identityData,
                  documentNumber: e.target.value
                })} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="Enter document number" data-id="element-3713" />
                  </div>

                  <div data-id="element-3714">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3715">
                      Upload Document *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer" data-id="element-3716">
                      <input type="file" accept="image/*,.pdf" onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'identity');
                  }} className="hidden" id="identity-upload" data-id="element-3717" />
                      <label htmlFor="identity-upload" className="cursor-pointer" data-id="element-3718">
                        {identityData.documentFile ? <div className="flex items-center justify-center space-x-2" data-id="element-3719">
                            <CheckCircle className="h-5 w-5 text-green-600" data-id="element-3720" />
                            <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-3721">
                              {identityData.documentFile.name}
                            </span>
                          </div> : <>
                            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" data-id="element-3722" />
                            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3723">
                              Click to upload or drag and drop
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1" data-id="element-3724">
                              PNG, JPG or PDF (max 5MB)
                            </p>
                          </>}
                      </label>
                    </div>
                  </div>
                </div>}

              {/* Ownership Verification */}
              {currentStep === 'ownership' && <div className="space-y-6" data-id="element-3725">
                  <div data-id="element-3726">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-3727">
                      Property Ownership
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3728">
                      Upload ownership documents for each property
                    </p>
                  </div>

                  {ownershipData.map((doc, index) => <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4" data-id="element-3729">
                      <div className="flex items-center justify-between" data-id="element-3730">
                        <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-3731">
                          Property {index + 1}
                        </h3>
                        {ownershipData.length > 1 && <button onClick={() => removeOwnershipDocument(index)} className="text-red-600 hover:text-red-700 dark:text-red-400" data-id="element-3732">
                            <X className="h-5 w-5" data-id="element-3733" />
                          </button>}
                      </div>

                      <div data-id="element-3734">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3735">
                          Document Type *
                        </label>
                        <select value={doc.documentType} onChange={e => {
                    const newOwnership = [...ownershipData];
                    newOwnership[index].documentType = e.target.value as any;
                    setOwnershipData(newOwnership);
                  }} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3736">
                          <option value="title_deed" data-id="element-3737">Title Deed</option>
                          <option value="certificate_of_occupancy" data-id="element-3738">
                            Certificate of Occupancy
                          </option>
                          <option value="purchase_agreement" data-id="element-3739">
                            Purchase Agreement
                          </option>
                        </select>
                      </div>

                      <div data-id="element-3740">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3741">
                          Property Address *
                        </label>
                        <input type="text" value={doc.propertyAddress} onChange={e => {
                    const newOwnership = [...ownershipData];
                    newOwnership[index].propertyAddress = e.target.value;
                    setOwnershipData(newOwnership);
                  }} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="Enter property address" data-id="element-3742" />
                      </div>

                      <div data-id="element-3743">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3744">
                          Upload Document *
                        </label>
                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer" data-id="element-3745">
                          <input type="file" accept="image/*,.pdf" onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file, 'ownership', index);
                    }} className="hidden" id={`ownership-upload-${index}`} data-id="element-3746" />
                          <label htmlFor={`ownership-upload-${index}`} className="cursor-pointer" data-id="element-3747">
                            {doc.documentFile ? <div className="flex items-center justify-center space-x-2" data-id="element-3748">
                                <CheckCircle className="h-5 w-5 text-green-600" data-id="element-3749" />
                                <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-3750">
                                  {doc.documentFile.name}
                                </span>
                              </div> : <>
                                <Upload className="h-6 w-6 text-gray-400 mx-auto mb-1" data-id="element-3751" />
                                <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-3752">
                                  Upload ownership document
                                </p>
                              </>}
                          </label>
                        </div>
                      </div>
                    </div>)}

                  <button onClick={addOwnershipDocument} className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:border-blue-500 dark:hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" data-id="element-3753">
                    + Add Another Property
                  </button>
                </div>}

              {/* Banking Information */}
              {currentStep === 'banking' && <div className="space-y-6" data-id="element-3754">
                  <div data-id="element-3755">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-3756">
                      Bank Account Verification
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3757">
                      Provide your bank details for rent payments
                    </p>
                  </div>

                  <div data-id="element-3758">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3759">
                      Bank Name *
                    </label>
                    <select value={bankingData.bankName} onChange={e => setBankingData({
                  ...bankingData,
                  bankName: e.target.value
                })} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3760">
                      <option value="" data-id="element-3761">Select bank</option>
                      <option value="GTBank" data-id="element-3762">GTBank</option>
                      <option value="Access Bank" data-id="element-3763">Access Bank</option>
                      <option value="Zenith Bank" data-id="element-3764">Zenith Bank</option>
                      <option value="First Bank" data-id="element-3765">First Bank</option>
                      <option value="UBA" data-id="element-3766">UBA</option>
                      <option value="Ecobank" data-id="element-3767">Ecobank</option>
                    </select>
                  </div>

                  <div data-id="element-3768">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3769">
                      Account Number *
                    </label>
                    <input type="text" value={bankingData.accountNumber} onChange={e => setBankingData({
                  ...bankingData,
                  accountNumber: e.target.value
                })} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="0123456789" maxLength={10} data-id="element-3770" />
                  </div>

                  <div data-id="element-3771">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3772">
                      Account Name *
                    </label>
                    <input type="text" value={bankingData.accountName} onChange={e => setBankingData({
                  ...bankingData,
                  accountName: e.target.value
                })} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="Account holder name" data-id="element-3773" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-3774">
                      Must match the name on your identity document
                    </p>
                  </div>
                </div>}

              {/* Review */}
              {currentStep === 'review' && <div className="space-y-6" data-id="element-3775">
                  <div data-id="element-3776">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-3777">
                      Review & Submit
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3778">
                      Please review your information before submitting
                    </p>
                  </div>

                  {/* Identity Summary */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-3779">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-3780">
                      <User className="h-5 w-5 mr-2 text-blue-600" data-id="element-3781" />
                      Identity Verification
                    </h3>
                    <div className="space-y-2 text-sm" data-id="element-3782">
                      <div className="flex justify-between" data-id="element-3783">
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-3784">
                          Document Type:
                        </span>
                        <span className="text-gray-900 dark:text-white font-medium" data-id="element-3785">
                          {identityData.documentType.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-3786">
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-3787">
                          Document Number:
                        </span>
                        <span className="text-gray-900 dark:text-white font-medium" data-id="element-3788">
                          {identityData.documentNumber}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-3789">
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-3790">
                          Document:
                        </span>
                        <span className="text-green-600 dark:text-green-400 flex items-center" data-id="element-3791">
                          <CheckCircle className="h-4 w-4 mr-1" data-id="element-3792" />
                          Uploaded
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Ownership Summary */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-3793">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-3794">
                      <Building className="h-5 w-5 mr-2 text-blue-600" data-id="element-3795" />
                      Property Ownership ({ownershipData.length})
                    </h3>
                    <div className="space-y-3" data-id="element-3796">
                      {ownershipData.map((doc, index) => <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-sm" data-id="element-3797">
                          <p className="text-gray-900 dark:text-white font-medium mb-1" data-id="element-3798">
                            {doc.propertyAddress}
                          </p>
                          <p className="text-gray-600 dark:text-gray-400 text-xs" data-id="element-3799">
                            {doc.documentType.replace(/_/g, ' ')} • Uploaded
                          </p>
                        </div>)}
                    </div>
                  </div>

                  {/* Banking Summary */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-3800">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center" data-id="element-3801">
                      <CreditCard className="h-5 w-5 mr-2 text-blue-600" data-id="element-3802" />
                      Bank Account
                    </h3>
                    <div className="space-y-2 text-sm" data-id="element-3803">
                      <div className="flex justify-between" data-id="element-3804">
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-3805">
                          Bank:
                        </span>
                        <span className="text-gray-900 dark:text-white font-medium" data-id="element-3806">
                          {bankingData.bankName}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-3807">
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-3808">
                          Account Number:
                        </span>
                        <span className="text-gray-900 dark:text-white font-medium" data-id="element-3809">
                          {bankingData.accountNumber}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-3810">
                        <span className="text-gray-600 dark:text-gray-400" data-id="element-3811">
                          Account Name:
                        </span>
                        <span className="text-gray-900 dark:text-white font-medium" data-id="element-3812">
                          {bankingData.accountName}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Consent */}
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4" data-id="element-3813">
                    <div className="flex items-start space-x-3" data-id="element-3814">
                      <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" data-id="element-3815" />
                      <div className="flex-1" data-id="element-3816">
                        <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-1" data-id="element-3817">
                          Verification Process
                        </h4>
                        <p className="text-xs text-yellow-800 dark:text-yellow-200 leading-relaxed" data-id="element-3818">
                          By submitting, you consent to Renta verifying your
                          documents with relevant authorities. Our team will
                          review your application within 24 hours and notify you
                          of the outcome.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between" data-id="element-3819">
            <button onClick={handleBack} disabled={currentStepIndex === 0} className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-3820">
              Back
            </button>

            {currentStep === 'review' ? <button onClick={handleSubmit} disabled={!canProceed() || isSubmitting} className="px-8 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center shadow-lg shadow-blue-200 dark:shadow-none" data-id="element-3821">
                {isSubmitting ? <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" data-id="element-3822"></div>
                    Submitting...
                  </> : <>
                    Submit for Verification
                    <ChevronRight className="ml-2 h-5 w-5" data-id="element-3823" />
                  </>}
              </button> : <button onClick={handleNext} disabled={!canProceed()} className="px-8 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center shadow-lg shadow-blue-200 dark:shadow-none" data-id="element-3824">
                Next
                <ChevronRight className="ml-2 h-5 w-5" data-id="element-3825" />
              </button>}
          </div>
        </div>
      </main>
    </div>;
}